/**
 * /feature-request-template — SEO landing page targeting the "feature
 * request template" / "feature request form template" / "product feature
 * request template" cluster.
 *
 * Differentiation angle: a feature request template — a form, a ticket, a
 * spreadsheet row — captures the ask once and then sits in an island,
 * disconnected from the product decisions it should inform. This page hands
 * the reader the actual template (the eight fields, spelled out) so a
 * searcher gets something usable, then shows that Draftlize links every
 * feature request to the relevant decision / spec cards: once accepted it's
 * traceable through to ship, and when a decision changes you can trace back
 * to the requests that rode on it.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and the
 * comparison pages. Chinese variant lives in FeatureRequestTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function FeatureRequestTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/feature-request-template"
      documentTitle="Feature Request Template (Linked to the Decision) · Draftlize"
      documentDescription="A feature request template you can copy — Title, Requester & date, Problem or job-to-be-done, Proposed solution, Use case & frequency, Impact & reach, Priority, Linked decision or spec. The catch: a feature request form in a tracker or a spreadsheet is an island, cut off from the product decisions it should feed. In Draftlize every feature request links to the relevant decision or spec card — traceable to ship once accepted, traceable back when a decision changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Feature Requests</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A feature request template<br/>
            <em>that goes somewhere.</em>
          </h1>
          <p className="lede">
            You came for a <em>feature request template</em> — the fields, the headings, something to drop into a form or a doc. It's below; take it. But know the failure mode: a feature request form in a tracker, a spreadsheet, or an intake ticket captures the ask once and then strands it. The request never connects to the product decision it's supposed to inform, so a good idea dies in a backlog and an accepted one loses the thread between "we'll do it" and what actually shipped. Draftlize gives you the same eight fields — and links each request to the decision and spec cards it touches, so it stays traceable from intake all the way to ship.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight fields. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole template. It works in a form, a tracker, or a doc — it's the same shape product teams have used for intake forever. Use it as-is. The next section is about what changes when each request stops being a row in a list and starts linking to the decisions it feeds.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Title',
              hint: 'Required',
              body: 'The ask in one line, phrased as the outcome — "Let users export a thread to PDF," not "PDF feature." If you can\'t name the outcome in a sentence, the request isn\'t shaped yet.',
            },
            {
              k: 'Requester & date',
              hint: 'Who / when',
              body: 'Who asked and when. Not for credit — for the follow-up. When the request resurfaces in triage, this is who you go back to for the detail the form didn\'t capture.',
            },
            {
              k: 'Problem or job-to-be-done',
              hint: 'The why',
              body: 'The underlying need, stated without the solution baked in. What is the user actually trying to get done, and what stops them today. This is the field that survives even when the proposed solution gets thrown out.',
            },
            {
              k: 'Proposed solution',
              hint: 'The how',
              body: 'How the requester imagines solving it — a starting point, not a commitment. Useful signal, but kept separate from the problem so you can accept the need while rejecting the specific fix.',
            },
            {
              k: 'Use case & frequency',
              hint: 'When it bites',
              body: 'The concrete situation it shows up in and how often. "Every Monday when I assemble the weekly report" beats "would be nice." Frequency is what separates a paper cut from a one-off.',
            },
            {
              k: 'Impact & reach',
              hint: 'How much',
              body: 'Who it affects and how badly — how many users, how acute the pain, what it unblocks. The field that lets you compare two requests that both sound reasonable in isolation.',
            },
            {
              k: 'Priority',
              hint: 'Rank',
              body: 'Where it sits against everything else — now, next, later, or won\'t-do. A priority is a comparison, not a feeling; it only means something relative to the other open requests.',
            },
            {
              k: 'Linked decision or spec',
              hint: 'What it touches',
              body: 'The product decision or spec this request informs or depends on. In a form this is a blank you\'ll leave empty. It\'s the field that decides whether the request goes anywhere after intake.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why a request form is an island — and how we wire each request into the decisions it should move.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a request form <em>is an island.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Intake captures the ask, then strands it</h3>
            <p>A feature request form is good at one thing: getting the ask out of someone's head and into a field. But the form is a dead end. It lives in a tracker, a spreadsheet, or a typeform export, with no thread connecting it to the product decision it's supposed to inform. Captured is not the same as considered.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Accepted requests lose the thread to ship</h3>
            <p>You triage a request and say yes. Now what? The decision to build it lives in one place, the spec in another, the original request in a third. By the time it ships, nobody can answer "did we actually solve what was asked?" — because the request and the work were never linked.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">When a decision changes, the requests go untraced</h3>
            <p>You reverse a product decision in week seven. Which feature requests rode on it? The ones you accepted because of the old call, the ones you declined because of it — all silently orphaned. Nothing links them back to the decision, so the reversal happens blind.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>wired in.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each request becomes a card, not a row</h3>
            <p>In Draftlize a feature request isn't a row in a spreadsheet — it's a typed card with those exact fields: Title, Problem, Proposed solution, Use case, Impact, Priority, and the link. Structured, addressable by ID, and citable from a decision or a spec instead of stranded in an intake tool.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Accept it, and it's traceable to ship</h3>
            <p>Because the request links to the decision and the spec it informs, accepting it isn't a status change in a vacuum — it's an edge in the graph. You can trace a shipped feature back to the request that asked for it, and answer "did we solve the actual job?" with a link, not a memory.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">A decision changes, linked requests flag stale</h3>
            <p>Because the link is real and not a note, reversing a decision turns every request that rested on it stale automatically — the way a build system invalidates everything downstream of a changed file. And an AI agent reads those requests over MCP before it drafts the next spec, so intake becomes context, not an archive.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A request form tells you what someone wants. It can't tell you whether the decision that would grant it still holds.
        </blockquote>
        <cite>Keep the eight fields. Let the substrate keep them connected.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a feature request?', a: 'A feature request is a structured record of something a user or stakeholder wants the product to do — the problem behind it, the proposed solution, the use case, and the impact. A good one captures the underlying job, not just the asked-for feature, so you can solve the real need rather than the literal request.' },
            { q: 'What should a feature request template include?', a: 'Title, problem, proposed solution, use case, impact, priority, and a link to the decision or spec it informs. The problem field matters most: a request that only states a solution ("add a dropdown") hides the job it is trying to do, and the job is what you actually prioritize against.' },
            { q: 'How do you prioritize feature requests?', a: 'Score them against a consistent framework — RICE, value versus effort, or weighted scoring — using the problem and impact, not the volume of asks. Ten people requesting the same workaround is one problem, not ten features. Rank by the job to be done, then decide.' },
            { q: 'How does Draftlize keep feature requests from getting orphaned?', a: 'Each request is a card linked to the decision and spec it informs, so accepting one is an edge in the graph, not a status change in a vacuum. Reverse a decision later and every request that rested on it flags itself stale automatically — the reversal stops happening blind.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start collecting requests <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project and let the agent file each request as a card — then link every one to the decisions and specs it touches, and keep the whole chain current for you.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
